import { MAIN_CATEGORY } from 'Constants/categories';
import { isPriceFigureInFractional, roundOffNumberForPrice, shouldNotIndexPriceRange } from './numbers';
import { camelize } from './strings';
import { getSlugs } from './url';
import {
    DECIMAL_RADIX,
    ORIGINAL_ITEMS,
    SUGGESTED_ITEMS
} from 'Constants/items';
import * as filtersHelper from 'Helpers/filters';

/** Context should have config, location and cookies
 * @param {Object} props: { params, location, }
 * @param {Object} context - { config: { get: }, cookies: { get } }
 */

const parsePathname = pathname => {
    let location = {};

    if (pathname) {
        const [firstSlug] = getSlugs(pathname);

        if (firstSlug && firstSlug.includes('_g')) {
            const [slug, id] = firstSlug.split('_g');
            const name = camelize(slug.split('-').join(' '));

            location = {
                id,
                name
            };
        }
    }

    return location;
};

const isMultipleValueSelected = make => make.includes(',');

const shouldAppendCount = (filters, categoryID) => {
    let shouldAddCount = true;
    const filterKeys = Object.keys(filters);
    const priceFilters = filterKeys.filter(k => k.startsWith('price'))?.length || 0;
    const isRefurbishedFilterApplied = filterKeys.indexOf('state') === -1;

    for (const [k, v] of Object.entries(filters)) {
        if (categoryID === MAIN_CATEGORY.CARS
            && (
                (k === 'make' && !isMultipleValueSelected(v))
                || (k === 'petrol' && !isMultipleValueSelected(v))
            )
            && filterKeys?.length === 1
        ) {
            // eslint-disable-next-line no-continue
            continue;
        }
        else if (k === 'make') {
            shouldAddCount = !!(filterKeys.indexOf('state') > -1) || [MAIN_CATEGORY.MOBILES, MAIN_CATEGORY.MOBILE_PHONES].indexOf(categoryID) > -1;
        }
        else if (k === 'model' && isMultipleValueSelected(v)) {
            shouldAddCount = false;
        }
        else if (filterKeys?.filter(k => k !== 'make' && k !== 'model')?.length === 1 && filterKeys?.filter(k => k === 'make' || k === 'model')?.length >= 1 && isRefurbishedFilterApplied) {
            shouldAddCount = false;
        }
        else if (filterKeys?.filter(k => k === 'make' || k === 'model')?.length <= 2 && isMultipleValueSelected(v)) {
            shouldAddCount = false;
        }
        else if (filterKeys?.length > 1 && priceFilters <= 2 && ((filterKeys?.length || 0) - priceFilters) > 1 && isRefurbishedFilterApplied) {
            shouldAddCount = false;
        }
        else if (k === 'year_min' || k === 'year_max' || k === 'first_owner' || k === 'petrol') {
            shouldAddCount = false;
        }
    }

    return shouldAddCount;
};

const routerParamsToSeoQuery = ({
    location: { query: { filter, page } = {}},
    params: { categoryID, geoID, text, centreName } = {},
    seoInformation,
    metadata
}) => {
    const seoQuery = {};

    if (categoryID) {
        seoQuery.categoryId = categoryID;
    }

    if (geoID) {
        seoQuery.locationId = geoID;
    }

    if (text) {
        seoQuery.query = text;
    }

    if (centreName) {
        seoQuery.locationName = centreName;
    }

    if (filter) {
        seoQuery.filter = filter;
    }

    const { seoInfo: { locationId, makeId } = {}} = seoInformation ?? {};

    if (locationId) {
        seoQuery.locationId = locationId;
    }

    if (makeId) {
        seoQuery.makeId = makeId;
    }

    const decodedFilters = filtersHelper.decodeRelevanceFilters(filter || '');
    const filterKeys = Object.keys(decodedFilters);

    // No need to format price filter for ACs and Fridges.
    if (categoryID === MAIN_CATEGORY.ACS || categoryID === MAIN_CATEGORY.FRIDGE) {
        seoQuery.filter = filter;
    }
    else if (
        categoryID === MAIN_CATEGORY.MOTORCYCLES
        && filter?.includes('price_')
        && (decodedFilters?.price_max || decodedFilters?.price_min)
    ) {
        if (decodedFilters?.price_max) {
            seoQuery.filter = seoQuery.filter.replace(
                /price_.*/g,
                `price_max_${roundOffNumberForPrice(decodedFilters?.price_max)?.literal}`
            );
        }
        else if (decodedFilters?.price_min) {
            seoQuery.filter = seoQuery.filter.replace(
                /price_.*/g,
                `price_min_${roundOffNumberForPrice(decodedFilters?.price_min)?.literal}`
            );
        }
    }
    else if (
        filter?.includes('price_')
        && (decodedFilters?.price_max || decodedFilters?.price_min)
    ) {
        if ( // when min and max is defined and the difference is > 0.9 && < 1.0
            decodedFilters?.price_max
            && decodedFilters?.price_min
            && shouldNotIndexPriceRange({
                min: parseInt(decodedFilters?.price_min, DECIMAL_RADIX),
                max: parseInt(decodedFilters?.price_max, DECIMAL_RADIX)
            })
        ) {
            seoQuery.filter = seoQuery.filter.replace(
                /price_.*/g,
                `price_max_${roundOffNumberForPrice(decodedFilters?.price_max)?.literal}`
            );
        }
        else if ( // when min and max is defined and the difference is not > 0.9 && < 1.0
            decodedFilters?.price_max
            && decodedFilters?.price_min
            && isPriceFigureInFractional(decodedFilters.price_max)
        ) {
            seoQuery.filter = seoQuery.filter.replace(
                /price_.*/g,
                `price_max_${roundOffNumberForPrice(decodedFilters?.price_max)?.literal}`
            );
        }
        else if ( // only min price is defined -> Round it off for UX purpose
            decodedFilters?.price_min
            && !decodedFilters?.price_max
            && isPriceFigureInFractional(decodedFilters.price_min)
        ) {
            seoQuery.filter = seoQuery.filter.replace(
                /price_.*/g,
                `price_min_${roundOffNumberForPrice(decodedFilters?.price_min)?.literal}`
            );
        }
        else if ( // only max price is defined -> Round it off for UX purpose
            decodedFilters?.price_max
            && !decodedFilters?.price_min
            && isPriceFigureInFractional(decodedFilters.price_max)
        ) {
            seoQuery.filter = seoQuery.filter.replace(
                /price_.*/g,
                `price_max_${roundOffNumberForPrice(decodedFilters?.price_max)?.literal}`
            );
        }
    }
    if (metadata?.count > 0 && (filterKeys.length === 1 && categoryID === MAIN_CATEGORY.MOTORCYCLES || shouldAppendCount(decodedFilters, categoryID))) {
        seoQuery.count = metadata?.count;
    }

    if (page > 1) {
        seoQuery.page_no = page;
    }

    return seoQuery;
};

const getLabelInfo = ({ totalItems, totalSuggestedAds, show_original_items,
    show_suggested_items, original_label, suggested_label, similarAdsCount }) => {
    let labelInfo;

    if (show_original_items || !show_suggested_items || similarAdsCount) {
        labelInfo = {
            itemsLabel: original_label,
            total: similarAdsCount ? 0 : totalItems,
            labelType: ORIGINAL_ITEMS
        };
    }
    else {
        labelInfo = {
            itemsLabel: suggested_label,
            total: totalSuggestedAds,
            labelType: SUGGESTED_ITEMS
        };
    }

    return labelInfo;
};

const itemCollection = (...args) => {
    Object.setPrototypeOf(args, itemCollection.prototype);
    return args;
};

itemCollection.prototype = Object.create(Array.prototype);
itemCollection.prototype.constructor = itemCollection;
itemCollection.prototype.setTotal = total => {
    if (total) {
        itemCollection.prototype.total = total;
        return;
    }
    delete itemCollection.prototype.total;
};
itemCollection.prototype.setTotalSuggestedAds = total => {
    if (total) {
        itemCollection.prototype.totalSuggestedAds = total;
        return;
    }
    delete itemCollection.prototype.totalSuggestedAds;
};
itemCollection.prototype.setCursor = cursor => {
    if (cursor) {
        itemCollection.prototype.cursor = cursor;
        return;
    }
    delete itemCollection.prototype.cursor;
};
itemCollection.prototype.setNextPageUrl = nextPageUrl => {
    if (nextPageUrl) {
        itemCollection.prototype.nextPageUrl = nextPageUrl;
        return;
    }
    delete itemCollection.prototype.nextPageUrl;
};
itemCollection.prototype.setNextSuggestedPageUrl = nextSuggestedPageUrl => {
    if (nextSuggestedPageUrl) {
        itemCollection.prototype.nextSuggestedPageUrl = nextSuggestedPageUrl;
        return;
    }
    delete itemCollection.prototype.nextSuggestedPageUrl;
};

export {
    getLabelInfo,
    parsePathname,
    routerParamsToSeoQuery,
    itemCollection
};
